import React from 'react';
import { useSelectAllClient } from '../../../shared/hooks/UseClient';
import { useSelectAllEmployee } from '../../../shared/hooks/UseEmployee';

const AdsActivityFilters = ({ filters, onFilterChange, onClearAll }) => {
  const [clientData] = useSelectAllClient();
  const [employeeData] = useSelectAllEmployee();

  const clients = clientData?.all_clients || [];
  const users = employeeData?.all_employees || [];

  const handleFilterChange = (key, value) => {
    onFilterChange({ [key]: value });
  };

  const clearFilters = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      onFilterChange({
        status: '',
        platform: '',
        assigned_to: '',
        client: '',
      });
    }
  };

  const hasActiveFilters = Object.values(filters).some(
    (f) => f && f !== '' && f !== 1 && f !== 10
  );

  return (
    <div className='filter-sidebar-container'>
      <div className='filter-header'>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h6 className='mb-0 fw-semibold'>
            <i className='fas fa-filter me-2 text-primary'></i>
            Filters
          </h6>
          {hasActiveFilters && (
            <button
              className='btn btn-sm btn-outline-danger'
              onClick={clearFilters}
              title='Clear all filters'
            >
              <i className='fas fa-times'></i>
            </button>
          )}
        </div>
      </div>

      <div className='filter-body'>
        <div className='filter-sections'>
          {/* Status Filter */}
          <div className='filter-section mb-3'>
            <label className='form-label fw-medium small text-dark'>
              Status
            </label>
            <select
              className='form-select form-select-sm'
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value=''>All Statuses</option>
              <option value='PENDING'>Pending</option>
              <option value='IN_PROGRESS'>In Progress</option>
              <option value='COMPLETED'>Completed</option>
              <option value='CANCELLED'>Cancelled</option>
            </select>
          </div>

          {/* Platform Filter */}
          <div className='filter-section mb-3'>
            <label className='form-label fw-medium small text-dark'>
              Platform
            </label>
            <select
              className='form-select form-select-sm'
              value={filters.platform || ''}
              onChange={(e) => handleFilterChange('platform', e.target.value)}
            >
              <option value=''>All Platforms</option>
              <option value='FACEBOOK'>Facebook</option>
              <option value='GOOGLE'>Google</option>
            </select>
          </div>

          {/* Assigned To Filter */}
          <div className='filter-section mb-3'>
            <label className='form-label fw-medium small text-dark'>
              Assigned To
            </label>
            <select
              className='form-select form-select-sm'
              value={filters.assigned_to || ''}
              onChange={(e) =>
                handleFilterChange('assigned_to', e.target.value)
              }
            >
              <option value=''>All Users</option>
              {users?.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Client Filter */}
          <div className='filter-section mb-3'>
            <label className='form-label fw-medium small text-dark'>
              Client
            </label>
            <select
              className='form-select form-select-sm'
              value={filters.client || ''}
              onChange={(e) => handleFilterChange('client', e.target.value)}
            >
              <option value=''>All Clients</option>
              {clients?.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className='active-filters mt-3 pt-3 border-top'>
            <small className='text-muted fw-medium'>Active Filters:</small>
            <div className='d-flex flex-wrap gap-1 mt-2'>
              {filters.status && (
                <span className='badge bg-primary position-relative'>
                  {filters.status}
                  <button
                    className='btn-close btn-close-white position-absolute top-0 start-100 translate-middle'
                    onClick={() => handleFilterChange('status', '')}
                    style={{ fontSize: '0.5em', width: '12px', height: '12px' }}
                    aria-label='Remove status filter'
                  ></button>
                </span>
              )}
              {filters.platform && (
                <span className='badge bg-info position-relative'>
                  {filters.platform}
                  <button
                    className='btn-close btn-close-white position-absolute top-0 start-100 translate-middle'
                    onClick={() => handleFilterChange('platform', '')}
                    style={{ fontSize: '0.5em', width: '12px', height: '12px' }}
                    aria-label='Remove platform filter'
                  ></button>
                </span>
              )}
              {filters.assigned_to && (
                <span className='badge bg-success position-relative'>
                  {users?.find((u) => u._id === filters.assigned_to)?.name}
                  <button
                    className='btn-close btn-close-white position-absolute top-0 start-100 translate-middle'
                    onClick={() => handleFilterChange('assigned_to', '')}
                    style={{ fontSize: '0.5em', width: '12px', height: '12px' }}
                    aria-label='Remove assigned user filter'
                  ></button>
                </span>
              )}
              {filters.client && (
                <span className='badge bg-warning position-relative'>
                  {clients?.find((c) => c._id === filters.client)?.name}
                  <button
                    className='btn-close btn-close-white position-absolute top-0 start-100 translate-middle'
                    onClick={() => handleFilterChange('client', '')}
                    style={{ fontSize: '0.5em', width: '12px', height: '12px' }}
                    aria-label='Remove client filter'
                  ></button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .filter-sidebar-container {
          background: transparent;
          border: none;
          overflow: visible;
          width: 100%;
        }

        .filter-header {
          padding: 0;
          border: none;
          background: transparent;
        }

        .filter-header h6 {
          color: #495057;
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .filter-sections {
          padding: 0;
        }

        .filter-section {
          position: relative;
        }

        .filter-section .form-label {
          margin-bottom: 0.5rem;
          color: #6c757d;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .filter-section .form-select {
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.875rem;
          padding: 0.5rem 0.75rem;
          background-color: white;
          transition: all 0.2s ease;
          box-shadow: none;
        }

        .filter-section .form-select:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
          outline: none;
        }

        .filter-section .form-select:hover {
          border-color: #adb5bd;
        }

        .active-filters {
          border-top: 1px solid #e9ecef;
          padding-top: 1rem;
        }

        .active-filters small {
          color: #6c757d;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .active-filters .badge {
          font-size: 0.75rem;
          padding: 0.4em 0.8em;
          border-radius: 20px;
          font-weight: 500;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }

        .active-filters .badge .btn-close {
          background: none;
          border: none;
          opacity: 0.8;
          cursor: pointer;
          padding: 0;
          margin: 0;
          line-height: 1;
        }

        .active-filters .badge .btn-close:hover {
          opacity: 1;
        }

        /* Button styling */
        .btn-outline-danger {
          border-color: #dc3545;
          color: #dc3545;
          font-size: 0.75rem;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
        }

        .btn-outline-danger:hover {
          background-color: #dc3545;
          color: white;
        }

        @media (max-width: 767.98px) {
          .filter-section {
            margin-bottom: 1rem;
          }

          .filter-section .form-select {
            font-size: 1rem;
            padding: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AdsActivityFilters;
